import React, { useState } from "react";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../api"; // your axios instance

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot | reset
  const [loading, setLoading] = useState(false);

  // Login form
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  // Forgot password form
  const [forgotEmail, setForgotEmail] = useState("");
  // Reset password form
  const [resetForm, setResetForm] = useState({
    email: "",
    token: "",
    newPassword: "",
  });

  // --- Handlers ---
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await login(loginForm.username, loginForm.password);
      navigate("/dashboard");
    } catch (err) {
      alert(err?.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return alert("Enter your email");
    try {
      setLoading(true);
      await api.put("/auth/forgotPassword", null, {
        params: { email: forgotEmail },
      });
      alert(
        "Password reset link sent. Enter your token below to reset your password."
      );
      // Switch to reset mode and prefill email
      setResetForm({ ...resetForm, email: forgotEmail });
      setMode("reset");
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Failed to send reset link");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    const { email, token, newPassword } = resetForm;
    if (!email || !token || !newPassword)
      return alert("All fields are required");
    try {
      setLoading(true);
      await api.put("/auth/resetPassword", null, {
        params: { email, token, newPassword },
      });
      alert("Password reset successful. Please login.");
      setMode("login");
      setResetForm({ email: "", token: "", newPassword: "" });
      setForgotEmail("");
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  // --- Render Forms ---
  return (
    <div className="max-w-md mx-auto mt-12 bg-white rounded-xl shadow-lg overflow-hidden p-6">
      {mode === "login" && (
        <>
          <h1 className="text-2xl font-bold mb-2">Welcome back</h1>
          <p className="text-sm text-gray-500 mb-6">
            Sign in to manage your SMS and view reports.
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <input
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="Username"
              value={loginForm.username}
              onChange={(e) =>
                setLoginForm({ ...loginForm, username: e.target.value })
              }
              required
            />
            <input
              type="password"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="Password"
              value={loginForm.password}
              onChange={(e) =>
                setLoginForm({ ...loginForm, password: e.target.value })
              }
              required
            />
            <button
              disabled={loading}
              className="w-full bg-emerald-600 text-white py-2 rounded hover:bg-emerald-700 transition"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="text-center text-sm text-gray-400 mt-4">
            <button className="underline" onClick={() => setMode("forgot")}>
              Forgot password?
            </button>
          </div>
        </>
      )}

      {mode === "forgot" && (
        <>
          <h1 className="text-2xl font-bold mb-2">Forgot Password</h1>
          <p className="text-sm text-gray-500 mb-6">
            Enter your email to receive a password reset token.
          </p>

          <form onSubmit={handleForgot} className="space-y-4">
            <input
              type="email"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="Email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              required
            />
            <button
              disabled={loading}
              className="w-full bg-emerald-600 text-white py-2 rounded hover:bg-emerald-700 transition"
            >
              {loading ? "Sending…" : "Send Reset Token"}
            </button>
          </form>

          <div className="text-center text-sm text-gray-400 mt-4">
            <button className="underline" onClick={() => setMode("login")}>
              Back to Login
            </button>
          </div>
        </>
      )}

      {mode === "reset" && (
        <>
          <h1 className="text-2xl font-bold mb-2">Reset Password</h1>
          <p className="text-sm text-gray-500 mb-6">
            Enter the token sent to your email and your new password.
          </p>

          <form onSubmit={handleReset} className="space-y-4">
            <input
              type="email"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="Email"
              value={resetForm.email}
              onChange={(e) =>
                setResetForm({ ...resetForm, email: e.target.value })
              }
              required
            />
            <input
              type="number"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="Token"
              value={resetForm.token}
              onChange={(e) =>
                setResetForm({ ...resetForm, token: e.target.value })
              }
              required
            />
            <input
              type="password"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="New Password"
              value={resetForm.newPassword}
              onChange={(e) =>
                setResetForm({ ...resetForm, newPassword: e.target.value })
              }
              required
            />
            <button
              disabled={loading}
              className="w-full bg-emerald-600 text-white py-2 rounded hover:bg-emerald-700 transition"
            >
              {loading ? "Resetting…" : "Reset Password"}
            </button>
          </form>

          <div className="text-center text-sm text-gray-400 mt-4">
            <button className="underline" onClick={() => setMode("login")}>
              Back to Login
            </button>
          </div>
        </>
      )}
    </div>
  );
}
